/**
 * useMobileAppPrompt composable
 *
 * Decides whether the dashboard should show the subtle "get the mobile app"
 * icon: only for signed-in users with no admin role, on a device whose store
 * listing is live (Android today). See src/utils/MobileApp.js for the rules.
 *
 * Reads two Vuex sources (same as usePermissions):
 *   - stSessionData        - session flags (isAdminInLocation, isSuperAdmin, ...)
 *   - stFullPermissionData - the complete /auth/permissions response (admin_level)
 * The session flags are available as soon as the session loads, so a scoped
 * admin is never flashed the icon while the permission payload is still loading.
 */

import { computed } from "vue";
import { useStore } from "vuex";
import {
  detectMobilePlatform,
  getMobileAppStoreUrl,
  isAdminSession,
  shouldOfferMobileApp,
} from "@/utils/MobileApp";

export function useMobileAppPrompt() {
  const store = useStore();

  // The device does not change during a page view, so detect it once.
  const platform = detectMobilePlatform();

  const showMobileAppPrompt = computed(() => {
    const session = store.getters.stSessionData || {};
    const permissions = store.getters.stFullPermissionData || {};

    return shouldOfferMobileApp({
      isSignedIn: Boolean(session.personId || session.personid),
      isAdmin: isAdminSession(session, permissions),
      platform,
    });
  });

  return {
    showMobileAppPrompt,
    mobileAppUrl: getMobileAppStoreUrl(platform),
    platform,
  };
}
